// src/components/billing/InvoiceManagement.js
import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { ConfirmationModal } from "../ui/ConfirmationModal";
import { apiService } from "../../services/api";
import toast from "react-hot-toast";
import {
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";

import { formatLocalDate } from "../../utils/helpers";

const InvoiceManagement = ({ invoices, onUpdate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [invoiceToProcess, setInvoiceToProcess] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [loadingInvoiceDetails, setLoadingInvoiceDetails] = useState(false);

  // Filter invoices based on search and status
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      !searchTerm ||
      invoice.club_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || invoice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const handleMarkAsPaid = async () => {
    if (!invoiceToProcess) return;

    setActionLoading(invoiceToProcess.id);
    try {
      await apiService.markInvoiceAsPaid(invoiceToProcess.id);
      toast.success("Invoice marked as paid!");
      setShowMarkPaidModal(false);
      setInvoiceToProcess(null);
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to mark invoice as paid");
    }
    setActionLoading(null);
  };

  const handleMarkAsPaidClick = (invoice) => {
    setInvoiceToProcess(invoice);
    setShowMarkPaidModal(true);
  };

  const handleSendReminder = async (invoiceId) => {
    setActionLoading(invoiceId);
    try {
      await apiService.sendInvoiceReminder(invoiceId);
      toast.success("Payment reminder sent!");
    } catch (error) {
      toast.error("Failed to send reminder");
    }
    setActionLoading(null);
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      // Get the PDF blob directly from the API service
      const pdfBlob = await apiService.downloadInvoice(invoiceId);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download invoice");
    }
  };

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
    setLoadingInvoiceDetails(true);

    try {
      const invoiceDetails = await apiService.getInvoice(invoice.id);

      // The API might return the invoice directly or wrapped in an object
      const invoiceData = invoiceDetails.invoice || invoiceDetails;
      setSelectedInvoiceDetails(invoiceData);
    } catch (error) {
      console.error("Error loading invoice details:", error);
      toast.error("Failed to load invoice details");
    } finally {
      setLoadingInvoiceDetails(false);
    }
  };

  const handleVoidInvoice = async () => {
    if (!invoiceToProcess) return;

    setActionLoading(invoiceToProcess.id);
    try {
      await apiService.voidInvoice(invoiceToProcess.id);
      toast.success("Invoice voided successfully!");
      setShowVoidModal(false);
      setInvoiceToProcess(null);
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to void invoice");
    }
    setActionLoading(null);
  };

  const handleVoidInvoiceClick = (invoice) => {
    setInvoiceToProcess(invoice);
    setShowVoidModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "green";
      case "overdue":
        return "red";
      case "pending":
        return "yellow";
      case "voided":
        return "gray";
      default:
        return "blue";
    }
  };

  const getStatusStats = () => {
    const stats = {
      total: invoices.length,
      paid: invoices.filter((inv) => inv.status === "paid").length,
      pending: invoices.filter((inv) => inv.status === "pending").length,
      overdue: invoices.filter((inv) => inv.status === "overdue").length,
      voided: invoices.filter((inv) => inv.status === "voided").length,
    };

    const totalAmount = invoices.reduce(
      (sum, inv) => sum + (inv.total_amount || 0),
      0,
    );
    const paidAmount = invoices
      .filter((inv) => inv.status === "paid")
      .reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const pendingAmount = invoices
      .filter((inv) => inv.status === "pending")
      .reduce((sum, inv) => sum + (inv.total_amount || 0), 0);

    return { ...stats, totalAmount, paidAmount, pendingAmount };
  };

  const stats = getStatusStats();

  const InvoiceDetailModal = () => {
    const invoice = selectedInvoiceDetails || selectedInvoice;

    return (
      <Modal
        isOpen={showInvoiceModal}
        title={`Invoice ${invoice?.invoice_number}`}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
          setSelectedInvoiceDetails(null);
        }}
        size="lg"
      >
        {loadingInvoiceDetails ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
            <span className="ml-2 text-gray-600">
              Loading invoice details...
            </span>
          </div>
        ) : invoice ? (
          <div className="space-y-6">
            {/* Invoice Header */}
            <div className="border-b border-gray-200 pb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium">
                    {invoice.clubs?.name || invoice.club_name}
                  </h3>
                  <p className="text-gray-600">
                    {invoice.clubs?.location || invoice.club_location}
                  </p>
                </div>
                <Badge variant={getStatusColor(invoice.status)}>
                  {invoice.status}
                </Badge>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Invoice Date:</span>
                  <span className="ml-2">
                    {formatLocalDate(
                      invoice.invoice_date || invoice.created_at,
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Due Date:</span>
                  <span className="ml-2">
                    {formatLocalDate(invoice.due_date)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Period:</span>
                  <span className="ml-2">
                    {formatLocalDate(invoice.billing_period_start)} -{" "}
                    {formatLocalDate(invoice.billing_period_end)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Amount:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(invoice.total_amount)}
                  </span>
                </div>
                {invoice.club_subscriptions?.subscription_plans && (
                  <div>
                    <span className="text-gray-500">Plan:</span>
                    <span className="ml-2">
                      {invoice.club_subscriptions.subscription_plans.name}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Line Items */}
            <div>
              <h4 className="font-medium mb-3">Invoice Details</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                {invoice.invoice_line_items &&
                invoice.invoice_line_items.length > 0 ? (
                  <div className="space-y-3">
                    {invoice.invoice_line_items.map((item, index) => (
                      <div
                        key={item.id || index}
                        className="flex justify-between"
                      >
                        <div>
                          <div className="font-medium">{item.description}</div>
                          <div className="text-sm text-gray-500">
                            Quantity: {item.quantity} ×{" "}
                            {formatCurrency(item.unit_price)}
                          </div>
                        </div>
                        <div className="font-medium">
                          {formatCurrency(item.total_price)}
                        </div>
                      </div>
                    ))}

                    {/* Totals */}
                    <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                      <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span>{formatCurrency(invoice.subtotal)}</span>
                      </div>
                      {invoice.tax_amount > 0 && (
                        <div className="flex justify-between">
                          <span>Tax</span>
                          <span>{formatCurrency(invoice.tax_amount)}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 pt-2">
                        <div className="flex justify-between font-medium text-lg">
                          <span>Total</span>
                          <span>{formatCurrency(invoice.total_amount)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Fallback for old invoice structure */}
                    <div className="flex justify-between">
                      <span>Usage Charges</span>
                      <span>{formatCurrency(invoice.total_amount)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-2">
                      <div className="flex justify-between font-medium">
                        <span>Total</span>
                        <span>{formatCurrency(invoice.total_amount)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Usage Snapshot */}
                {invoice.usage_snapshot && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h5 className="font-medium text-gray-900 mb-2">
                      Usage Summary
                    </h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        Billing Period:{" "}
                        {invoice.usage_snapshot.billing_period_start} to{" "}
                        {invoice.usage_snapshot.billing_period_end}
                      </div>
                      <div>
                        Billable Players:{" "}
                        {invoice.usage_snapshot.billable_players}
                      </div>
                      <div>
                        Rate per Player:{" "}
                        {formatCurrency(invoice.usage_snapshot.rate_per_player)}
                      </div>
                      <div>
                        Billing Method:{" "}
                        {invoice.usage_snapshot.billing_method?.replace(
                          /_/g,
                          " ",
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4 border-t border-gray-200">
              <Button
                onClick={() =>
                  handleDownloadInvoice(invoice.id, invoice.invoice_number)
                }
                variant="outline"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Download PDF
              </Button>

              {invoice.status === "pending" && (
                <>
                  <Button
                    onClick={() => handleMarkAsPaidClick(invoice)}
                    loading={actionLoading === invoice.id}
                  >
                    Mark as Paid
                  </Button>
                  <Button
                    onClick={() => handleSendReminder(invoice.id)}
                    variant="secondary"
                    loading={actionLoading === invoice.id}
                  >
                    <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                    Send Reminder
                  </Button>
                </>
              )}

              {invoice.status !== "voided" && invoice.status !== "paid" && (
                <Button
                  onClick={() => handleVoidInvoiceClick(invoice)}
                  variant="danger"
                  loading={actionLoading === invoice.id}
                >
                  Void Invoice
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Failed to load invoice details
          </div>
        )}
      </Modal>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {stats.total}
              </div>
              <div className="text-sm text-gray-500">Total Invoices</div>
              <div className="text-xs text-gray-400 mt-1">
                {formatCurrency(stats.totalAmount)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.paid}
              </div>
              <div className="text-sm text-gray-500">Paid</div>
              <div className="text-xs text-gray-400 mt-1">
                {formatCurrency(stats.paidAmount)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {stats.pending}
              </div>
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-xs text-gray-400 mt-1">
                {formatCurrency(stats.pendingAmount)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {stats.overdue}
              </div>
              <div className="text-sm text-gray-500">Overdue</div>
              <div className="text-xs text-gray-400 mt-1">Action Required</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-slate-500 focus:border-slate-500"
                />
              </div>

              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-4 w-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 pr-10 min-w-32 focus:ring-slate-500 focus:border-slate-500"
                >
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="overdue">Overdue</option>
                  <option value="voided">Voided</option>
                </select>
              </div>
            </div>

            <div className="text-sm text-gray-500">
              Showing {filteredInvoices.length} of {invoices.length} invoices
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice List */}
      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No invoices found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "No invoices have been generated yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div>
                        <div className="font-medium text-gray-900">
                          {invoice.invoice_number}
                        </div>
                        <div className="text-sm text-gray-600">
                          {invoice.club_name}
                        </div>
                      </div>
                      <Badge variant={getStatusColor(invoice.status)}>
                        {invoice.status}
                      </Badge>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Created: {formatLocalDate(invoice.created_at)} • Due:{" "}
                      {formatLocalDate(invoice.due_date)} • Period:{" "}
                      {formatLocalDate(invoice.billing_period_start)} -{" "}
                      {formatLocalDate(invoice.billing_period_end)}
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="font-medium text-gray-900">
                        {formatCurrency(invoice.total_amount)}
                      </div>
                      {invoice.status === "overdue" && (
                        <div className="text-xs text-red-600 flex items-center">
                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                          Overdue
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewInvoice(invoice)}
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        View
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleDownloadInvoice(
                            invoice.id,
                            invoice.invoice_number,
                          )
                        }
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                        Download
                      </Button>

                      {invoice.status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() => handleMarkAsPaidClick(invoice)}
                          loading={actionLoading === invoice.id}
                        >
                          Mark Paid
                        </Button>
                      )}

                      {(invoice.status === "pending" ||
                        invoice.status === "overdue") && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleSendReminder(invoice.id)}
                          loading={actionLoading === invoice.id}
                        >
                          <PaperAirplaneIcon className="w-4 h-4 mr-1" />
                          Remind
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Modal */}
      {showInvoiceModal && <InvoiceDetailModal />}

      {/* Mark as Paid Confirmation Modal */}
      <ConfirmationModal
        isOpen={showMarkPaidModal}
        onClose={() => {
          setShowMarkPaidModal(false);
          setInvoiceToProcess(null);
        }}
        onConfirm={handleMarkAsPaid}
        title="Mark Invoice as Paid"
        message={
          invoiceToProcess
            ? `Mark invoice ${invoiceToProcess.invoice_number} as paid? This will update the invoice status and cannot be undone.`
            : "Mark this invoice as paid?"
        }
        confirmText="Mark as Paid"
        loading={actionLoading === invoiceToProcess?.id}
      />

      {/* Void Invoice Confirmation Modal */}
      <ConfirmationModal
        isOpen={showVoidModal}
        onClose={() => {
          setShowVoidModal(false);
          setInvoiceToProcess(null);
        }}
        onConfirm={handleVoidInvoice}
        title="Void Invoice"
        message={
          invoiceToProcess
            ? `Are you sure you want to void invoice ${invoiceToProcess.invoice_number}? This action cannot be undone.`
            : "Are you sure you want to void this invoice? This action cannot be undone."
        }
        confirmText="Void Invoice"
        confirmVariant="danger"
        loading={actionLoading === invoiceToProcess?.id}
      />
    </div>
  );
};

export default InvoiceManagement;
